import EditIcon from '@mui/icons-material/Edit'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useGetList, useNotify, usePermissions, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { parseVetFilterFromSearch, petMatchesVetClinic } from '../clinics/clinicAssociation'
import { DataTruncationNotice } from '../components/DataTruncationNotice'
import PageHeader from '../components/PageHeader'
import ToneChip from '../components/ToneChip'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { formatDobForDisplay } from '../pets/dob'
import type { StatusTone } from '../themes/statusColors'
import { Device, Pet, User } from '../types'
import { authHeaders } from '../utils/api'
import { canEditClientProfile } from './clientProfile'
import EditClientDialog from './EditClientDialog'
import { getOwnerStatus, type OwnerStatus } from './ownerStatus'

interface OwnerWithPets extends User {
  pets: Pet[]
  devices: Device[]
}

// Account states are workflow, not clinical priority (UI-STYLE-GUIDE §3):
// active is the quiet positive tone, pending/disabled are neutral. This was a
// saturated filled-green "Active" on every row and an orange "Pending".
const STATUS_CHIP: Record<OwnerStatus, { tone: StatusTone; key: string }> = {
  active: { tone: 'positive', key: 'pos.owners.status_active' },
  pending: { tone: 'neutral', key: 'pos.owners.status_pending' },
  disabled: { tone: 'neutral', key: 'pos.owners.status_disabled' },
}

const OwnerStatusBadge: React.FC<{ status: OwnerStatus }> = ({ status }) => {
  const translate = useTranslate()
  const { tone, key } = STATUS_CHIP[status]
  return <ToneChip tone={tone} label={translate(key)} data-status={status} />
}

const SPECIES_LABEL: Record<string, string> = {
  dogs: 'Dog',
  cats: 'Cat',
  horses: 'Horse',
  cows: 'Cow',
  rabbits: 'Rabbit',
  guinea_pigs: 'Guinea Pig',
  ferrets: 'Ferret',
}

const formatSpecies = (species?: string | null): string => {
  if (!species) return '—'
  return SPECIES_LABEL[species] ?? species.charAt(0).toUpperCase() + species.slice(1)
}

// dob is stored as Unix SECONDS (a number, despite the string type — see
// RegisterOwner/PetDatagrid). Guard against ISO strings and empty values.
// dob means a CALENDAR DAY, not an instant — render it via the shared UTC-based
// formatter so this list agrees with the edit form (see src/pets/dob.ts).
const formatDob = (dob?: string | number | null): string => formatDobForDisplay(dob) || '—'

// owner/pet timestamps from Payload are ISO strings.
const formatIsoDate = (iso?: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="caption" color="text.primary" sx={{ wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
)

const OwnersList = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const notify = useNotify()
  const { permissions } = usePermissions()
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null)
  const [ownersList, setOwnersList] = useState<OwnerWithPets[]>([])
  const [resendingId, setResendingId] = useState<number | null>(null)
  const [editingClient, setEditingClient] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const canManage = ['vet', 'vetadmin'].includes(permissions?.role)

  // Re-send the claim/activation email to a pending owner the clinic created.
  const handleResendInvite = async (ownerId: number) => {
    setResendingId(ownerId)
    try {
      const res = await deadlineFetch('/api/users/resend-owner-invite', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ownerId }),
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        notify(
          data?.alreadyActive ? 'pos.owners.resend_already_active' : 'pos.owners.resend_success',
          { type: data?.alreadyActive ? 'info' : 'success' },
        )
        // Reflect the new send time locally so the detail panel updates.
        if (!data?.alreadyActive) {
          const nowIso = new Date().toISOString()
          setOwnersList((list) =>
            list.map((o) => (o.id === ownerId ? { ...o, invitationSentAt: nowIso } : o)),
          )
        }
      } else if (res.status === 429) {
        notify('pos.owners.resend_rate_limited', { type: 'warning' })
      } else {
        notify('pos.owners.resend_error', { type: 'error' })
      }
    } catch {
      notify('pos.owners.resend_error', { type: 'error' })
    } finally {
      setResendingId(null)
    }
  }
  const clinicVetFilter = useMemo(() => parseVetFilterFromSearch(window.location.search), [])

  // Fetch all pets with owner information
  const {
    data: pets = [],
    isLoading: petsLoading,
    error: petsError,
    refetch: refetchPets,
  } = useGetList<Pet>('pets', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'updatedAt', order: 'DESC' },
    meta: { params: { depth: 2 } }, // Ensure owner + owner.vets are populated
  })

  // Fetch all devices
  const {
    data: devices = [],
    total: devicesTotal,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useGetList<Device>('devices', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'id', order: 'ASC' },
    meta: { params: { depth: 1 } }, // Ensure pet object is populated
  })

  // Aggregate owners with their pets and devices.
  //
  // Every owner the clinic created or is linked to is shown — including
  // PENDING owners and owners whose devices have no metrics yet. We must NOT
  // gate owner visibility on metric data, or a vet who just created a profile
  // sees "0 owners" and assumes the save failed (see owner-pending-status req).
  useEffect(() => {
    if (petsLoading || devicesLoading) return

    const ownersMap = new Map<number, OwnerWithPets>()

    const clinicPets = pets.filter((pet) => petMatchesVetClinic(pet, clinicVetFilter))

    clinicPets.forEach((pet) => {
      const ownerObj = typeof pet.owner === 'object' ? (pet.owner as User) : null
      if (!ownerObj) return
      const ownerId = ownerObj.id

      if (!ownersMap.has(ownerId)) {
        ownersMap.set(ownerId, { ...(ownerObj as User), pets: [], devices: [] })
      }
      ownersMap.get(ownerId)!.pets.push(pet)
    })

    // Associate devices with owners through their pets
    devices.forEach((device) => {
      const petObj = typeof device.pet === 'object' ? (device.pet as Pet) : null
      const petId = petObj ? petObj.id : (device.pet as number)
      const pet = clinicPets.find((p) => p.id === petId)
      if (!pet) return

      const ownerObj = typeof pet.owner === 'object' ? (pet.owner as User) : null
      if (!ownerObj) return
      const owner = ownersMap.get(ownerObj.id)
      if (owner) owner.devices.push(device)
    })

    const ownersList = Array.from(ownersMap.values()).sort((a, b) => {
      const aName = a.name || a.email || ''
      const bName = b.name || b.email || ''
      return aName.localeCompare(bName)
    })

    setOwnersList(ownersList)

    // Auto-select first owner if none selected
    if (!selectedOwnerId && ownersList.length > 0) {
      setSelectedOwnerId(ownersList[0].id)
    }
  }, [pets, devices, petsLoading, devicesLoading, selectedOwnerId, clinicVetFilter])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedOwners = ownersList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const selectedOwner = ownersList.find((o) => o.id === selectedOwnerId)

  const pendingCount = ownersList.filter((o) => getOwnerStatus(o) === 'pending').length
  const activeCount = ownersList.filter((o) => getOwnerStatus(o) === 'active').length

  if (petsLoading || devicesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  // A failed load must NOT render as "0 clients" — the comment above the
  // aggregation warns a vet who just created a profile would assume the
  // save failed. Same principle for a network failure: say so, offer retry.
  if (petsError || devicesError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
        <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        <Button
          variant="outlined"
          onClick={() => {
            refetchPets()
            refetchDevices()
          }}
        >
          {translate('ra.action.refresh')}
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={translate('pos.owners.title')}
        subtitle={
          ownersList.length === 0
            ? translate('pos.owners.total_count', { count: 0 })
            : translate('pos.owners.total_summary', {
                count: ownersList.length,
                active: activeCount,
                pending: pendingCount,
              })
        }
        actions={
          ['vet', 'vetadmin'].includes(permissions?.role) ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/register-owner')}
            >
              {translate('pos.register_owner.title')}
            </Button>
          ) : undefined
        }
      />
      {/* The clinic-wide devices read is server-capped (ada-core
          DEVICES_MAX_LIMIT) and this list sorts by id ASC, so truncation drops
          the NEWEST devices — a just-activated sensor would be missing from a
          client's row with no other sign. Same notice as the dashboard and the
          pets list, from one component. */}
      <DataTruncationNotice
        lists={[{ resource: 'devices', loaded: devices.length, total: devicesTotal }]}
      />
      {/* Master/detail. The list was a fixed 450px-min card in a row that
          never wrapped, so a phone scrolled the whole page sideways (84px).
          Stacks below md; side by side from md. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: 'flex-start',
        }}
      >
        {/* Left Panel: Owners List */}
        <Card
          sx={{
            width: { xs: '100%', md: 440 },
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'flex-start' },
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1 } }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('resources.pets.fields.owner')}</TableCell>
                    <TableCell align="right">{translate('pos.owners.pets_count')}</TableCell>
                    <TableCell align="right">{translate('pos.owners.devices_count')}</TableCell>
                    <TableCell align="center">{translate('pos.owners.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOwners.map((owner) => (
                    <TableRow
                      key={owner.id}
                      hover
                      selected={owner.id === selectedOwnerId}
                      onClick={() => setSelectedOwnerId(owner.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor:
                          owner.id === selectedOwnerId ? 'rgba(79, 60, 201, 0.08)' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {owner.name || owner.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {/* Owner contact caption — always the email. Previously
                              `owner.clinic_name || owner.email`, but pet owners
                              (role=user) never legitimately carry clinic_name, so
                              that chain rendered email by accident and would have
                              shown a stray clinic name where contact info is meant
                              (clinic-name-only display audit, 2026-06-06; see
                              src/pets/getVetClinicName.ts for the ownership rule). */}
                            {owner.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Plain counts — they were filled purple and navy chips,
                          two loud colors carrying no status at all. */}
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {owner.pets.length}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {owner.devices.length}
                      </TableCell>
                      <TableCell align="center">
                        <OwnerStatusBadge status={getOwnerStatus(owner)} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {ownersList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('pos.owners.no_owners_yet')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={ownersList.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: '52px',
                  paddingLeft: 1,
                  paddingRight: 0,
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: 1,
                },
                '& .MuiTablePagination-actions button': {
                  color: 'text.primary',
                  padding: '4px',
                },
                '& .MuiTablePagination-actions button.Mui-disabled': {
                  color: 'text.disabled',
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Right Panel: Selected Owner's Pets and Devices */}
        <Card sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {selectedOwner ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ mb: 0, mr: 0.5 }}>
                    {selectedOwner.name || selectedOwner.email}
                  </Typography>
                  <OwnerStatusBadge status={getOwnerStatus(selectedOwner)} />
                  {canEditClientProfile(permissions?.role) && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingClient(true)}
                      data-testid="edit-client-button"
                    >
                      {translate('pos.client_profile.edit_client')}
                    </Button>
                  )}
                  {canManage && getOwnerStatus(selectedOwner) === 'pending' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SendIcon />}
                      disabled={resendingId === selectedOwner.id}
                      onClick={() => handleResendInvite(selectedOwner.id)}
                      sx={{ ml: 'auto' }}
                    >
                      {translate('pos.owners.resend_invite')}
                    </Button>
                  )}
                </Box>
                {/* Owner contact + account meta (lets a vet confirm a pending
                  profile was created, per the pending-owner requirement) */}
                <Box
                  sx={{
                    mt: 1,
                    mb: 3,
                    p: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <InfoRow
                    label={translate('resources.pets.fields.owner_email')}
                    value={selectedOwner.email}
                  />
                  <InfoRow label={translate('pos.owners.phone')} value={selectedOwner.phone} />
                  <InfoRow
                    label={translate('pos.owners.created')}
                    value={formatIsoDate(selectedOwner.createdAt)}
                  />
                  <InfoRow
                    label={translate('pos.owners.invitation_label')}
                    value={
                      getOwnerStatus(selectedOwner) === 'pending'
                        ? selectedOwner.invitationSentAt
                          ? translate('pos.owners.invitation_sent_on', {
                              date: formatIsoDate(selectedOwner.invitationSentAt),
                            })
                          : translate('pos.owners.invitation_awaiting')
                        : translate('pos.owners.invitation_confirmed')
                    }
                  />
                </Box>

                {/* Pets Section */}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  {translate('resources.pets.name', { smart_count: 2 })} (
                  {selectedOwner.pets.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('resources.pets.fields.pet_name')}</TableCell>
                        <TableCell>{translate('resources.pets.fields.species')}</TableCell>
                        <TableCell>{translate('resources.pets.fields.breed')}</TableCell>
                        <TableCell>{translate('resources.pets.fields.sex')}</TableCell>
                        <TableCell>{translate('resources.pets.fields.dob')}</TableCell>
                        <TableCell>{translate('resources.pets.fields.weight')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOwner.pets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {translate('pos.owners.no_pets_yet')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedOwner.pets.map((pet) => (
                          <TableRow key={pet.id}>
                            <TableCell>{pet.name || '—'}</TableCell>
                            <TableCell>{formatSpecies(pet.species)}</TableCell>
                            <TableCell>{pet.breed || '—'}</TableCell>
                            <TableCell>{pet.gender || '—'}</TableCell>
                            <TableCell>{formatDob(pet.dob)}</TableCell>
                            <TableCell>{pet.weight || '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Devices Section */}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  {translate('resources.devices.name', { smart_count: 2 })} (
                  {selectedOwner.devices.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('resources.devices.fields.name')}</TableCell>
                        <TableCell>{translate('resources.devices.fields.pet')}</TableCell>
                        <TableCell>{translate('resources.devices.fields.type')}</TableCell>
                        <TableCell>{translate('resources.devices.fields.start_time')}</TableCell>
                        <TableCell>{translate('pos.owners.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOwner.devices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {translate('pos.owners.no_devices_yet')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedOwner.devices.map((device) => {
                          const pet = selectedOwner.pets.find(
                            (p) =>
                              p.id ===
                              (typeof device.pet === 'object' ? device.pet.id : device.pet),
                          )
                          const activated = Boolean(device.activatedAt || device.start_time)
                          return (
                            <TableRow key={device.id}>
                              <TableCell>{device.name}</TableCell>
                              <TableCell>{pet?.name || '—'}</TableCell>
                              <TableCell>{device.type || '—'}</TableCell>
                              <TableCell>
                                {device.start_time
                                  ? new Date(device.start_time * 1000).toLocaleDateString()
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                <ToneChip
                                  tone={activated ? 'positive' : 'neutral'}
                                  label={translate(
                                    activated
                                      ? 'pos.owners.device_active'
                                      : 'pos.owners.device_inactive',
                                  )}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {ownersList.length === 0
                  ? translate('pos.owners.no_owners_yet')
                  : translate('pos.owners.select_owner')}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Keyed on the selected client so switching clients re-seeds the form
          state instead of carrying the previous client's values over. */}
        {selectedOwner && editingClient && (
          <EditClientDialog
            key={selectedOwner.id}
            open
            ownerId={selectedOwner.id}
            initialName={selectedOwner.name || ''}
            initialPhone={selectedOwner.phone || ''}
            onClose={() => setEditingClient(false)}
            // The Clients page derives its rows from the PETS query (owners are
            // aggregated out of `pet.owner`), so refreshing the pets list is what
            // pulls the renamed client back in.
            onSaved={() => refetchPets()}
          />
        )}
      </Box>
    </Box>
  )
}

export default OwnersList
